import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiCheckCircle, FiXCircle, FiLoader, FiMail, FiCpu, FiRefreshCw, FiVideo } from 'react-icons/fi';
import './HealthCheck.css';

const HealthCheck = () => {
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [peertubeStatus, setPeertubeStatus] = useState(null);
  const [emailTestStatus, setEmailTestStatus] = useState(null);
  const [aiTestStatus, setAiTestStatus] = useState(null);
  const [aiQueryText, setAiQueryText] = useState('Привет, как дела?');
  const [loading, setLoading] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingAi, setTestingAi] = useState(false);

  useEffect(() => {
    performChecks();
  }, []);

  const performChecks = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/health/');
      if (response.data.smtp) setSmtpStatus(response.data.smtp);
      if (response.data.openai) setAiStatus(response.data.openai);
      if (response.data.peertube) setPeertubeStatus(response.data.peertube);
    } catch (error) {
      toast.error('Ошибка при проверке подключений');
    } finally {
      setLoading(false);
    }
  };

  const testEmailSending = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post('/api/health/', { test_email: true });
      if (response.data.email_test) {
        setEmailTestStatus(response.data.email_test);
        if (response.data.email_test.status === 'success') {
          toast.success('Тестовое письмо отправлено');
        } else {
          toast.error(response.data.email_test.message);
        }
      }
    } catch (error) {
      toast.error('Ошибка при отправке тестового письма');
    } finally {
      setTestingEmail(false);
    }
  };

  const testAiQuery = async () => {
    if (!aiQueryText.trim()) {
      toast.error('Введите текст запроса');
      return;
    }
    setTestingAi(true);
    try {
      const response = await axios.post('/api/health/', { 
        test_ai_query: true,
        query_text: aiQueryText.trim()
      });
      if (response.data.ai_test) {
        setAiTestStatus(response.data.ai_test);
        if (response.data.ai_test.status === 'success') {
          toast.success('Запрос к ИИ выполнен успешно');
        } else {
          toast.error(response.data.ai_test.message);
        }
      }
    } catch (error) {
      toast.error('Ошибка при запросе к ИИ');
    } finally {
      setTestingAi(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'success') return <FiCheckCircle className="status-icon success" />;
    if (status === 'error') return <FiXCircle className="status-icon error" />;
    return <FiLoader className="status-icon loading" />;
  };

  const getStatusText = (status) => {
    if (status === 'success') return 'Успешно';
    if (status === 'error') return 'Ошибка';
    return 'Проверка...';
  };

  return (
    <div className="health-check-page">
      <div className="page-header">
        <h1>Проверка подключений</h1>
        <button className="btn btn-primary" onClick={performChecks} disabled={loading}>
          <FiRefreshCw className={loading ? 'spinning' : ''} /> Обновить
        </button>
      </div>

      <div className="health-checks">
        {/* SMTP */}
        <div className="health-check-card">
          <div className="check-header">
            <div className="check-title">
              <FiMail className="check-icon" />
              <h2>Почта (SMTP)</h2>
            </div>
            {smtpStatus && (
              <div className={`status-badge ${smtpStatus.status}`}>
                {getStatusIcon(smtpStatus.status)}
                <span>{getStatusText(smtpStatus.status)}</span>
              </div>
            )}
          </div>
          {loading && !smtpStatus && (
            <div className="check-loading">
              <FiLoader className="spinning" /> Проверка...
            </div>
          )}
          {smtpStatus && (
            <>
              <div className={`check-message ${smtpStatus.status}`}>{smtpStatus.message}</div>
              {smtpStatus.details && Object.keys(smtpStatus.details).length > 0 && (
                <div className="check-details">
                  <h3>Параметры</h3>
                  <ul>
                    {Object.entries(smtpStatus.details).map(([key, value]) => (
                      <li key={key}><strong>{key}:</strong> {String(value)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {smtpStatus.log && smtpStatus.log.length > 0 && (
                <div className="check-log">
                  <h3>Подробный лог</h3>
                  <ul className="log-list">
                    {smtpStatus.log.map((entry, idx) => (
                      <li key={idx} className={entry.success ? 'log-ok' : 'log-fail'}>
                        <span className="log-step">Шаг {entry.step}</span>
                        <span className="log-text">{entry.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {smtpStatus.status === 'success' && (
                <div className="check-actions">
                  <button className="btn btn-outline" onClick={testEmailSending} disabled={testingEmail}>
                    {testingEmail ? <><FiLoader className="spinning" /> Отправка...</> : <><FiMail /> Тестовое письмо</>}
                  </button>
                </div>
              )}
              {emailTestStatus && (
                <div className={`email-test-result ${emailTestStatus.status}`}>
                  <strong>Тест отправки:</strong> {emailTestStatus.message}
                  {emailTestStatus.details?.recipient && (
                    <div className="test-details">Получатель: {emailTestStatus.details.recipient}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* ИИ (ChadGPT / OpenAI) */}
        <div className="health-check-card">
          <div className="check-header">
            <div className="check-title">
              <FiCpu className="check-icon" />
              <h2>ИИ (ChadGPT / OpenAI)</h2>
            </div>
            {aiStatus && (
              <div className={`status-badge ${aiStatus.status}`}>
                {getStatusIcon(aiStatus.status)}
                <span>{getStatusText(aiStatus.status)}</span>
              </div>
            )}
          </div>
          {loading && !aiStatus && (
            <div className="check-loading">
              <FiLoader className="spinning" /> Проверка...
            </div>
          )}
          {aiStatus && (
            <>
              <div className={`check-message ${aiStatus.status}`}>{aiStatus.message}</div>
              {aiStatus.details && Object.keys(aiStatus.details).length > 0 && (
                <div className="check-details">
                  <h3>Параметры</h3>
                  <ul>
                    {Object.entries(aiStatus.details).map(([key, value]) => (
                      <li key={key}><strong>{key}:</strong> {String(value)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {aiStatus.log && aiStatus.log.length > 0 && (
                <div className="check-log">
                  <h3>Подробный лог</h3>
                  <ul className="log-list">
                    {aiStatus.log.map((entry, idx) => (
                      <li key={idx} className={entry.success ? 'log-ok' : 'log-fail'}>
                        <span className="log-step">Шаг {entry.step}</span>
                        <span className="log-text">{entry.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aiStatus.status === 'success' && (
                <div className="check-actions">
                  <div className="ai-query-section">
                    <h3>Тестовый запрос к ИИ</h3>
                    <textarea
                      className="form-textarea"
                      value={aiQueryText}
                      onChange={(e) => setAiQueryText(e.target.value)}
                      placeholder="Введите текст запроса..."
                      rows="3"
                      style={{ marginBottom: '10px' }}
                    />
                    <button 
                      className="btn btn-outline" 
                      onClick={testAiQuery} 
                      disabled={testingAi}
                    >
                      {testingAi ? <><FiLoader className="spinning" /> Отправка...</> : <>Отправить запрос</>}
                    </button>
                  </div>
                  {aiTestStatus && (
                    <div className={`ai-test-result ${aiTestStatus.status}`}>
                      <div className="test-query">
                        <strong>Запрос:</strong> {aiTestStatus.query}
                      </div>
                      <div className="test-response">
                        <strong>Ответ ИИ:</strong>
                        <div className="response-text">{aiTestStatus.response || aiTestStatus.message}</div>
                      </div>
                      {aiTestStatus.details && Object.keys(aiTestStatus.details).length > 0 && (
                        <div className="test-details">
                          {Object.entries(aiTestStatus.details).map(([key, value]) => (
                            <div key={key}><strong>{key}:</strong> {String(value)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="health-check-card">
          <div className="check-header">
            <div className="check-title">
              <FiVideo className="check-icon" />
              <h2>PeerTube</h2>
            </div>
            {peertubeStatus && (
              <div className={`status-badge ${peertubeStatus.status}`}>
                {getStatusIcon(peertubeStatus.status)}
                <span>{getStatusText(peertubeStatus.status)}</span>
              </div>
            )}
          </div>
          {loading && !peertubeStatus && (
            <div className="check-loading">
              <FiLoader className="spinning" /> Проверка...
            </div>
          )}
          {peertubeStatus && (
            <>
              <div className={`check-message ${peertubeStatus.status}`}>{peertubeStatus.message}</div>
              {peertubeStatus.details && Object.keys(peertubeStatus.details).length > 0 && (
                <div className="check-details">
                  <h3>Параметры</h3>
                  <ul>
                    {Object.entries(peertubeStatus.details).map(([key, value]) => (
                      <li key={key}><strong>{key}:</strong> {String(value)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HealthCheck;
